import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Card,
  Field,
  Icon,
  Input,
  Note,
  RadioCard,
  RadioGroup,
  Skeleton,
} from '@repo/design-system';
import type { IconName } from '@repo/design-system';
import { fontSans, ink, inkOn, space, typeScale, weight } from '@repo/design-tokens';
import {
  checkTwinAvailability,
  createTwin,
  getOfferedTwinTypes,
  getTwinById,
} from '@repo/front-end-services';
import type { AdminTwin, OfferedTwinType } from '@repo/frontend-types';
import { IconDisc, visuallyHidden } from '@repo/ui';

import Config from '../../common/Config';
import {
  CREATE_FIELDS,
  bidAside,
  isIconName,
  isSameCreate,
  missingFields,
  nameError,
  nameForCreate,
  normaliseBidPart,
  problemsByField,
  refusedSummary,
  stripLineBreaks,
  typeColumns,
  type CreateField,
} from './twinCreateForm';
import { TWIN_REQUEST_TIMEOUT_MS } from './twinDetailsForm';
import { useTwinAvailability } from './useTwinAvailability';

interface Props {
  className?: string;
  organisationId: number;
  /** The organisation's BID — the twin's BID starts `{orgBid}-`. */
  orgBid: string;
  /** The organisation's display name, when known; the heading falls back to `orgBid`. */
  organisationName?: string;
  /** Resolves the bearer token for each request. A rejection reads as that request failing. */
  getToken: () => Promise<string>;
  /** The twin exists. Called once, with the twin as the server stored it. */
  onCreated: (twin: AdminTwin) => void;
  /** Back to the twin list — offered when whether the twin was created cannot be told. */
  onGoToTwins: () => void;
}

/** An offered type whose icon is one the kit draws. */
type ShownType = Omit<OfferedTwinType, 'icon'> & { icon: IconName };

/** The offered types, while they are read. `failed` is NOT `[]`: that would claim none are offered. */
type TypesState = 'loading' | 'failed' | ShownType[];

type FieldErrors = Partial<Record<CreateField, string>>;

/** A sentence under the form, and whether it offers the way back to the twin list. */
interface FormNote {
  text: string;
  goToTwins?: boolean;
}

const NOTHING_CREATED = 'Couldn’t create the twin. Nothing was created — try again.';
const UNKNOWN_OUTCOME =
  'We couldn’t tell whether the twin was created. Check the twin list before trying again.';

/** *Couldn't create the twin.* followed by each reason as its own sentence, in the server's words. */
const refusedNote = (reasons: string[]): string =>
  [
    'Couldn\u2019t create the twin.',
    ...reasons.filter(Boolean).map(r => (/[.!?]$/.test(r) ? r : `${r}.`)),
  ].join(' ');

/**
 * Each type with its own icon, or a plain one where the table names a glyph the kit does not have.
 * Resolved once per read — never in render, which runs on every keystroke — and warned once per row.
 */
const withKnownIcons = (offered: OfferedTwinType[]): ShownType[] =>
  offered.map(type => {
    if (isIconName(type.icon)) return { ...type, icon: type.icon };
    // eslint-disable-next-line no-console
    console.warn('[onboarding-create] offered type has an unknown icon', {
      id: type.id,
      icon: type.icon,
    });
    return { ...type, icon: 'building' };
  });

/** What a read-back found, and — when it could not tell — which step it could not make. */
type ReadBack =
  | { kind: 'created'; twin: AdminTwin }
  | { kind: 'none' }
  | { kind: 'other'; message: string }
  | { kind: 'unknown'; step: 'availability' | 'twin-read' };

/**
 * Whether a `failed` create may have landed. No status (a timeout, the network) or a 5xx may have
 * reached the insert; any other status is a definite refusal — nothing was written.
 */
const mayHaveLanded = (status: number | undefined): boolean =>
  status === undefined || status >= 500;

/**
 * **Create a twin** — the wizard's create mode (`twin-details-page-create`; *Twin Details Page*
 * frame 01): Name, BID and Type, and nothing written until **Create Twin**.
 *
 * What it reads before the press is the types table (`GET /api/twin-templates/offered`) and the
 * as-you-type check (`GET /api/twins/availability`); what it writes is one `POST /api/twins`. The
 * server's rules are the only rules: the form shows the server's answers in the server's words, and
 * a check that could not be made never reads as *Available*.
 *
 * **A create whose outcome is unknown is resolved before anything is said** (a timeout may have
 * landed). The read-back asks availability for the BID first, because `getTwinById` answers `null`
 * for both "no such twin" and "the read failed": *available* means nothing was created, *taken*
 * means read the twin and check it is this create, and no answer means we cannot tell — and say so.
 */
const OnboardingTwinCreate: React.FC<Props> = ({
  className,
  organisationId,
  orgBid,
  organisationName,
  getToken,
  onCreated,
  onGoToTwins,
}) => {
  const orgLabel = organisationName || orgBid;
  const [name, setName] = useState('');
  const [bidPart, setBidPart] = useState('');
  const [typeId, setTypeId] = useState<number | null>(null);
  const [types, setTypes] = useState<TypesState>('loading');
  /** Errors from the last press — missing fields or the server's refusal. Cleared as a field changes. */
  const [pressErrors, setPressErrors] = useState<FieldErrors>({});
  const [formNote, setFormNote] = useState<FormNote | null>(null);
  /** `creating`: the POST is in flight. `checking`: learning whether a failed one landed. */
  const [phase, setPhase] = useState<'idle' | 'creating' | 'checking'>('idle');
  const busy = phase !== 'idle';
  const busyRef = useRef(false);
  /** A create this mount sent may have landed: a later *taken* on its BID is read back first. */
  const unknownCreatesRef = useRef<Array<{ bid: string; name: string }>>([]);
  /** Every attempt on `bid` whose outcome was never learned (the name may differ between them). */
  const unresolvedFor = (bid: string) => unknownCreatesRef.current.filter(r => r.bid === bid);
  const forgetUnresolved = (bid: string) => {
    unknownCreatesRef.current = unknownCreatesRef.current.filter(r => r.bid !== bid);
  };
  /**
   * This press wrote nothing — but an EARLIER attempt on the same BID may still have landed, so
   * "nothing was created" would be false: say the outcome is still unknown instead.
   */
  const nothingThisTime = (bid: string): FormNote =>
    unresolvedFor(bid).length > 0
      ? { text: UNKNOWN_OUTCOME, goToTwins: true }
      : { text: NOTHING_CREATED };

  /** The token getter, read when a request is made: a new identity must not re-read the types. */
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  /** Still on screen: an answer that arrives after the person has left changes nothing. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const bidRef = useRef<HTMLInputElement | null>(null);
  const typeRef = useRef<HTMLDivElement | null>(null);

  const wholeBid = bidPart ? `${orgBid}-${bidPart}` : '';
  const checkedName = nameForCreate(name);
  const availability = useTwinAvailability({
    getToken,
    organisationId,
    bid: wholeBid,
    name: checkedName,
  });

  // ---- The offered types (Failure States, row 1) ----
  const loadGenRef = useRef(0);
  const loadTypes = useCallback(async () => {
    const gen = ++loadGenRef.current;
    setTypes('loading');
    let offered: OfferedTwinType[] | undefined;
    let cause: 'token' | 'request' = 'request';
    try {
      const token = await getTokenRef.current().catch(() => {
        cause = 'token';
        return undefined;
      });
      offered = token ? await getOfferedTwinTypes(Config.middlewareApi, token) : undefined;
    } catch {
      offered = undefined;
    }
    if (gen !== loadGenRef.current) return;
    if (offered === undefined) {
      // eslint-disable-next-line no-console
      console.error('[onboarding-create] offered types read failed', { cause });
      setTypes('failed');
      return;
    }
    if (offered.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('[onboarding-create] no offered twin types');
    }
    setTypes(withKnownIcons(offered));
  }, []);
  useEffect(() => {
    void loadTypes();
    return () => {
      loadGenRef.current += 1;
    };
  }, [loadTypes]);

  // ---- What each field shows ----
  const bidView = bidAside(availability.bid, wholeBid);
  const nameCheckError = nameError(availability.name);
  const errors: Record<CreateField, string | null> = {
    name: pressErrors.name ?? nameCheckError,
    bid: pressErrors.bid ?? bidView.error,
    type: pressErrors.type ?? null,
  };
  // A field in error never also says *Available*: the server's refusal is the newer answer.
  const bidShownAside = errors.bid ? null : bidView.aside;

  // ---- Said, not only shown (spec R4.5): once per change of a field's settled state ----
  const [politeText, setPoliteText] = useState('');
  const announcedRef = useRef<{ bid: string; name: string }>({ bid: '', name: '' });
  const bidSaid =
    availability.bid === 'idle' || availability.bid === 'pending'
      ? ''
      : errors.bid
        ? `BID: ${errors.bid}`
        : bidView.aside?.tone === 'success'
          ? 'BID available'
          : 'Couldn’t check the BID';
  const nameSaid = nameCheckError ? `Name: ${nameCheckError}` : '';
  useEffect(() => {
    const said: string[] = [];
    if (bidSaid !== announcedRef.current.bid) {
      announcedRef.current.bid = bidSaid;
      if (bidSaid) said.push(bidSaid);
    }
    if (nameSaid !== announcedRef.current.name) {
      announcedRef.current.name = nameSaid;
      if (nameSaid) said.push(nameSaid);
    }
    if (said.length > 0) setPoliteText(said.join('. '));
    // A field going back to pending clears the region, so the same answer for the next value is a
    // change of text and is said again.
    else if (!bidSaid && !nameSaid) setPoliteText('');
  }, [bidSaid, nameSaid]);

  /*
   * Focus moves AFTER the errors are drawn, so the field is announced with its message (Block A):
   * focusing in the same handler as the state update lands before the error is in the field's
   * description. A counter, so pressing twice with the same field in error moves focus again.
   */
  const [focusTarget, setFocusTarget] = useState<{ field: CreateField; n: number } | null>(null);
  const focusField = (field: CreateField) =>
    setFocusTarget(prev => ({ field, n: (prev?.n ?? 0) + 1 }));
  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget.field === 'name') nameRef.current?.focus();
    else if (focusTarget.field === 'bid') bidRef.current?.focus();
    else {
      // The first card; with no cards (types loading, failed or none offered) the slot itself,
      // or its *Try again*, so focus never stays on the button with nothing said.
      const slot = typeRef.current;
      const target =
        slot?.querySelector<HTMLElement>('input[type="radio"], button') ?? slot ?? null;
      target?.focus();
    }
  }, [focusTarget]);

  /*
   * Upper-casing rewrites the value, which sends the caret to the end in every browser: a letter
   * typed mid-BID would then push the next one to the end. The caret is put back where it was —
   * except after a pasted prefix was stripped, where the end is right.
   */
  const caretRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = caretRef.current;
    caretRef.current = null;
    const input = bidRef.current;
    if (at === null || !input || document.activeElement !== input) return;
    input.setSelectionRange(at, at);
  }, [bidPart]);

  const clearPressError = (field: CreateField) => {
    setPressErrors(prev => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /**
   * Did a create of unknown outcome land? `created` with the twin, `none` when the BID is still
   * free, `other` with the server's words when someone else holds it, `unknown` when we cannot tell.
   * A twin found under the BID is this session's when it matches ANY of `requests` — the attempt
   * just made, or an earlier one whose outcome was never learned (the name may have been edited
   * since).
   */
  const readBack = async (
    token: string,
    bid: string,
    requests: ReadonlyArray<{ bid: string; name: string }>,
  ): Promise<ReadBack> => {
    const check = await checkTwinAvailability(Config.middlewareApi, token, {
      organisationId,
      bid,
    });
    const answer = check?.bid;
    if (!answer) return { kind: 'unknown', step: 'availability' };
    if (answer.status === 'available') return { kind: 'none' };
    if (answer.status === 'invalid') return { kind: 'none' };
    const twin = await getTwinById(Config.middlewareApi, token, bid, {
      timeout: TWIN_REQUEST_TIMEOUT_MS,
    });
    if (!twin) return { kind: 'unknown', step: 'twin-read' };
    return requests.some(request => isSameCreate(twin, { organisationId, ...request }))
      ? { kind: 'created', twin }
      : { kind: 'other', message: answer.message };
  };

  /** The twin exists: hand over — unless the person has already left the page. */
  const handOver = (twin: AdminTwin) => {
    forgetUnresolved(twin.bid);
    if (mountedRef.current) onCreated(twin);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busyRef.current) return;
    setFormNote(null);

    const missing = missingFields({ name, bidPart, typeId });
    if (missing.length > 0) {
      setPressErrors(Object.fromEntries(missing.map(m => [m.field, m.message])));
      setFormNote({ text: refusedNote([refusedSummary(missing.map(m => m.field))]) });
      focusField(missing[0].field);
      return;
    }

    const request = { bid: `${orgBid}-${bidPart}`, name: nameForCreate(name) };
    busyRef.current = true;
    setPhase('creating');
    setPressErrors({});
    try {
      let token: string;
      try {
        token = await getTokenRef.current();
      } catch (error) {
        // Nothing was sent, so nothing can have been created. The error's name only — never the
        // object, which can carry the session.
        // eslint-disable-next-line no-console
        console.error('[onboarding-create] create token unavailable', {
          name: (error as Error | null)?.name,
        });
        setFormNote(nothingThisTime(request.bid));
        return;
      }
      const result = await createTwin(Config.middlewareApi, token, {
        organisation_id: organisationId,
        bid: request.bid,
        name: request.name,
        twin_template_id: typeId,
        // As the retired dialog sent them, so a twin created here reads the same downstream. No
        // timezone and no position: those come from the address (create-rules).
        orientation: '0',
        altitude: 0,
      });

      if (result.kind === 'created') {
        handOver(result.twin);
        return;
      }

      const earlier = unresolvedFor(request.bid);
      const takenAfterUnknown =
        result.kind === 'refused' &&
        result.status === 409 &&
        earlier.length > 0 &&
        result.problems.some(p => p.field === 'bid');

      if (result.kind === 'failed') {
        // eslint-disable-next-line no-console
        console.error('[onboarding-create] create failed', { status: result.status });
        if (!mayHaveLanded(result.status)) {
          // A definite refusal (401, 403, a refusal with no field list): nothing was written.
          setFormNote(nothingThisTime(request.bid));
          return;
        }
        // Kept until a read-back finds the twin: a create the server commits AFTER the read-back
        // looked is still caught by the next press's 409.
        if (!earlier.some(r => r.name === request.name)) {
          unknownCreatesRef.current = [...unknownCreatesRef.current, request];
        }
      }

      if (result.kind === 'failed' || takenAfterUnknown) {
        setPhase('checking');
        setPoliteText('Checking whether the twin was created…');
        const landed = await readBack(token, request.bid, [request, ...earlier]);
        if (landed.kind === 'created') {
          handOver(landed.twin);
          return;
        }
        if (landed.kind === 'unknown') {
          // eslint-disable-next-line no-console
          console.error('[onboarding-create] create outcome unknown', {
            createStatus: result.status,
            step: landed.step,
          });
          setFormNote({ text: UNKNOWN_OUTCOME, goToTwins: true });
          return;
        }
        if (landed.kind === 'none' && result.kind === 'failed') {
          setFormNote({ text: NOTHING_CREATED });
          return;
        }
        if (landed.kind === 'other' && result.kind === 'failed') {
          // Someone else's twin holds the BID: none of this visit's attempts on it landed.
          forgetUnresolved(request.bid);
          setPressErrors({ bid: landed.message });
          setFormNote({ text: refusedNote([refusedSummary(['bid'])]) });
          focusField('bid');
          return;
        }
        // A 409 the read-back did not explain: show the server's refusal as it stands.
      }

      if (result.kind === 'refused') {
        const byField = problemsByField(result.problems);
        const fieldErrors: FieldErrors = {};
        for (const field of CREATE_FIELDS) {
          const message = byField[field];
          if (message) fieldErrors[field] = message;
        }
        setPressErrors(fieldErrors);
        const inError = CREATE_FIELDS.filter(f => fieldErrors[f]);
        setFormNote({ text: refusedNote([refusedSummary(inError), ...byField.form]) });
        if (inError.length > 0) focusField(inError[0]);
      }
    } finally {
      busyRef.current = false;
      setPhase('idle');
    }
  };

  const typeList = Array.isArray(types) ? types : [];

  return (
    <form className={className} onSubmit={submit} noValidate aria-label="Create a twin">
      <div className="create-head">
        <IconDisc tone="aqua" size={64} className="create-disc">
          <Icon name="wand-magic-sparkles" variant="solid" size="xl" />
        </IconDisc>
        <h2>Create a twin in {orgLabel}</h2>
        <p className="create-sub">
          Three things to start. The address and opening hours come straight after.
        </p>
      </div>

      <Card surface="on-glass" size="lg" className="create-card">
        <Field
          label="Name"
          aside={`Unique in ${orgLabel} · you can change it later`}
          error={errors.name ?? undefined}
        >
          {wiring => (
            <Input
              {...wiring}
              ref={nameRef}
              invalid={errors.name != null}
              value={name}
              maxLength={100}
              autoComplete="off"
              readOnly={busy}
              onChange={e => {
                setName(stripLineBreaks(e.target.value));
                clearPressError('name');
              }}
            />
          )}
        </Field>

        <Field
          label="BID"
          aside={bidShownAside?.text}
          asideTone={bidShownAside?.tone}
          error={errors.bid ?? undefined}
          helper="The twin's key in TwinLabs and BI. It can't change after you create the twin."
          permanent
        >
          {wiring => (
            <Input
              {...wiring}
              ref={bidRef}
              invalid={errors.bid != null}
              fixedPrefix={`${orgBid}-`}
              value={bidPart}
              autoComplete="off"
              spellCheck={false}
              readOnly={busy}
              onChange={e => {
                const raw = e.target.value;
                const next = normaliseBidPart(raw, orgBid);
                // Same length ⇒ only the case changed: keep the caret where it was.
                caretRef.current = next.length === raw.length ? e.target.selectionStart : null;
                setBidPart(next);
                clearPressError('bid');
              }}
            />
          )}
        </Field>

        <Field
          label="Type"
          aside={types === 'loading' ? 'Loading types…' : 'Can’t change after you create the twin'}
          error={errors.type ?? undefined}
        >
          {wiring => (
            <div ref={typeRef} className="type-slot" tabIndex={-1}>
              {types === 'loading' ? (
                <div className="type-placeholders" aria-hidden="true">
                  <Skeleton shape="block" flush />
                  <Skeleton shape="block" flush />
                  <Skeleton shape="block" flush />
                </div>
              ) : types === 'failed' ? (
                <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                  Couldn&rsquo;t load the twin types.{' '}
                  <Button
                    type="button"
                    variant="text"
                    size="inline"
                    onClick={() => void loadTypes()}
                  >
                    Try again
                  </Button>
                </Note>
              ) : typeList.length === 0 ? (
                <Note tone="neutral" icon={<Icon name="circle-info" size="xs" />}>
                  No twin types are offered yet. Ask the TwinLabs team to offer one in AdminDB.
                </Note>
              ) : (
                <RadioGroup
                  aria-labelledby={wiring.labelId}
                  aria-describedby={wiring['aria-describedby']}
                  aria-invalid={errors.type != null || undefined}
                  variant="card"
                  columns={typeColumns(typeList.length)}
                  value={typeId === null ? '' : String(typeId)}
                  onChange={value => {
                    if (busyRef.current) return;
                    setTypeId(Number(value));
                    clearPressError('type');
                  }}
                >
                  {typeList.map(type => (
                    <RadioCard
                      key={type.id}
                      variant="selection"
                      value={String(type.id)}
                      icon={type.icon}
                      title={type.name}
                      description={type.short_description}
                    />
                  ))}
                </RadioGroup>
              )}
            </div>
          )}
        </Field>
      </Card>

      {formNote && (
        <Note tone="error" role="alert" className="create-note">
          {formNote.text}
          {formNote.goToTwins && (
            <>
              {' '}
              <Button type="button" variant="text" size="inline" onClick={onGoToTwins}>
                Go to your twins
              </Button>
            </>
          )}
        </Note>
      )}

      <div className="create-foot">
        <span className="create-promise">
          <Icon name="shield-halved" variant="solid" size="xs" className="create-shield" />
          Nothing is created until you press Create Twin
        </span>
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          iconLeft={<Icon name="wand-magic-sparkles" variant="solid" size="xs" />}
        >
          {phase === 'creating' ? 'Creating…' : phase === 'checking' ? 'Checking…' : 'Create Twin'}
        </Button>
      </div>
      {/* Shown; said through the polite region below, which is always mounted. */}
      {phase === 'checking' && (
        <p className="create-checking" aria-hidden="true">
          Checking whether the twin was created&hellip;
        </p>
      )}

      <span role="status" aria-live="polite" style={visuallyHidden}>
        {politeText}
      </span>
    </form>
  );
};

/** Frame 01's column: 720 px wide, centred in the content panel. */
const CREATE_WIDTH = '720px';

export default styled(OnboardingTwinCreate)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space[5]};
  width: 100%;
  max-width: ${CREATE_WIDTH};
  margin: 0 auto;
  font-family: ${fontSans};

  .create-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${space[2]};
    text-align: center;
  }

  .create-head h2 {
    margin: 0;
    font-size: ${typeScale.title3.fontSize};
    line-height: ${typeScale.title3.lineHeight};
    font-weight: ${weight.semibold};
    color: ${ink[1]};
  }

  .create-sub {
    margin: 0;
    font-size: ${typeScale.body1.fontSize};
    line-height: ${typeScale.body1.lineHeight};
    color: ${ink[2]};
  }

  .create-card {
    width: 100%;
    gap: ${space[4]};
  }

  .type-placeholders {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${space[2]};
  }

  .create-note {
    width: 100%;
  }

  .create-foot {
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${space[3]};
  }

  .create-promise {
    display: inline-flex;
    align-items: center;
    gap: ${space[2]};
    /* Frame 01 draws 13 / 18, off the ramp: the nearest step. */
    font-size: ${typeScale.caption1.fontSize};
    line-height: ${typeScale.caption1.lineHeight};
    font-weight: ${weight.medium};
    color: ${ink[2]};
  }

  .create-shield {
    color: ${inkOn.mint};
  }

  .create-foot > button {
    margin-left: auto;
  }

  .create-checking {
    margin: 0;
    font-size: ${typeScale.caption1.fontSize};
    color: ${ink[2]};
  }
`;
